import { Adjust } from "@awesome-cordova-plugins/adjust";
import { Capacitor } from "@capacitor/core";
import { AppleProviderResponse, GoogleLoginResponseOnline } from "@capgo/capacitor-social-login";
import { zodResolver } from "@hookform/resolvers/zod";
import { Typography, Box, makeStyles } from "@material-ui/core";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilStateLoadable } from "recoil";

import AppleLoginButton from "@/components/AppleLoginButton";
import { AppLoadError, LoadFailure } from "@/components/AppLoadError";
import { AuthErrorSummary } from "@/components/AuthFeedback";
import { CustomButton } from "@/components/Button";
import { CustomForm } from "@/components/Form";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { Loading } from "@/components/Loading";
import { TextLink } from "@/components/TextLink";
import { useDevice } from "@/hooks/useDevice";
import { EntranceActions, EntranceTemplate } from "@/layouts/EntranceTemplate";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { isNativeApp } from "@/utilities/capacitorLogic";
import { clearExpoRegistration } from "@/utilities/expoRegistration";
import { consumeIntent } from "@/utilities/intentNavigator";
import { loginSchema, LoginFormData } from "@/validations/login";
import { SessionState, UserState } from "@/view_models/atoms";

const useStyles = makeStyles(() => ({
  social: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    "&&& button": {
      maxWidth: "none",
      borderColor: theme.palette.semantic["border/strong"],
      color: theme.palette.text.primary,
      [theme.breakpoints.up("md")]: { fontSize: 16 },
    },
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "24px 0",
    fontSize: 12,
    color: theme.palette.text.secondary,
    "&::before, &::after": {
      content: "\"\"",
      height: 1,
      flex: 1,
      backgroundColor: theme.palette.semantic["border/default"],
    },
  },
  fields: { display: "flex", flexDirection: "column", gap: 16 },
  signup: {
    marginTop: 16,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
}));

// Adjust Attribution から network を取得（取得できた場合のみ返却）
// 検証環境ではAdjustを初期化しないため、ネイティブアプリにて適切に値が返らない。
// またフェイルセーフとしてタイムアウトを設けてAPI呼び出しをブロックしないようにする
const getAdjustNetwork = async (): Promise<string | undefined> => {
  if (!isNativeApp) return undefined;

  const fetch = async () => {
    try {
      const hasGetter = typeof (Adjust as any)?.getAttribution === "function";
      if (!hasGetter) return undefined;
      const attribution = await Adjust.getAttribution();
      const v = attribution?.network?.trim();
      return v || undefined;
    } catch (_) {
      return undefined;
    }
  };

  const timeoutMs = 800; // 0.8s 以内に返らなければ未送信扱い
  const timeout = new Promise<string | undefined>((resolve) => setTimeout(() => resolve(undefined), timeoutMs));
  return Promise.race([fetch(), timeout]);
};

// APIの呼び出し
const postLogin = async (data: { email: string; password: string }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginPost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "メールアドレスもしくはパスワードが違います",
      };
    }
    if (e.response?.status === 429) {
      return {
        success: false,
        error: "ログインの試行回数が上限に達しました。時間をおいて再度お試しください",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

// Googleアカウント用ログインAPIの呼び出し
const postLoginWithGoogle = async (data: { social_login_response: GoogleLoginResponseOnline }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginGooglePost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "アカウントが存在しません。先にアカウントを登録してください。",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

// Appleアカウント用ログインAPIの呼び出し
const postLoginWithApple = async (data: { social_login_response: AppleProviderResponse }) => {
  const fp = PropallyApiFactory();
  try {
    const adjust_network = await getAdjustNetwork();
    await fp.v1LoginApplePost(
      {
        ...data,
        ...(adjust_network ? { adjust_network } : {}),
      },
      { withCredentials: true }
    );
    return { success: true, error: "" };
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        success: false,
        error: "アカウントが存在しません。先にアカウントを登録してください。",
      };
    }
    return {
      success: false,
      error: "ネットワークエラーです。しばらくたってからお試しください",
    };
  }
};

// formの型はzodスキーマから生成
export type PostLoginRequestBody = LoginFormData;

export const LoginPage: React.FC = () => {
  const [session, setSession] = useRecoilState(SessionState);
  const [loadable] = useRecoilStateLoadable(UserState);
  const [error, setError] = React.useState<string>("");
  const [submitting, setSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const [loadFailure, setLoadFailure] = React.useState<LoadFailure | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PostLoginRequestBody>({
    resolver: zodResolver(loginSchema),
    mode: "onTouched",
    shouldFocusError: true,
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const history = useHistory();
  const classes = useStyles();
  const { isMobile } = useDevice();

  const submitLogin = async (request: () => ReturnType<typeof postLogin>) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError("");
    try {
      const result = await request();
      setSession({ logged_in: result.success });
      setError(result.error);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  const onClickLogin = (data: LoginFormData) =>
    submitLogin(() => postLogin({ email: data.email, password: data.password }));
  const handleSuccessGoogle = (socialLoginResponse: GoogleLoginResponseOnline) =>
    submitLogin(() => postLoginWithGoogle({ social_login_response: socialLoginResponse }));
  const handleSuccessApple = (socialLoginResponse: AppleProviderResponse) =>
    submitLogin(() => postLoginWithApple({ social_login_response: socialLoginResponse }));
  const handleError = () => setError("外部サービスでのログインに失敗しました");

  React.useEffect(() => {
    if (loadable.state === "hasValue" && loadable.contents !== null) {
      const user = loadable.contents;
      if (!user.family_name || !user.first_name || !user.email || !user.phone_number) {
        history.push("/first_account");
      }
    }
    if (session.logged_in) {
      history.push("/first_account");
    }
  }, [loadable.contents]);

  // エキスポ会場の登録導線（Trello #241）は新規登録専用。既存アカウントでログインする人には適用しないため、
  // ログイン画面を開いた時点で端末の目印を消す
  React.useEffect(() => {
    clearExpoRegistration();
  }, []);

  const [checkedSession, setCheckedSession] = React.useState(false);
  const [pendingIntent] = React.useState(consumeIntent);
  const pendingRef = React.useRef<string | null>(pendingIntent);

  React.useEffect(() => {
    (async () => {
      try {
        const adjust_network = await getAdjustNetwork();
        await PropallyApiFactory().v1SessionGet(adjust_network, { withCredentials: true });
        const dest = pendingRef.current ?? "/mypage";
        history.replace(dest);
      } catch (e: any) {
        if (e.response?.status !== 401) setLoadFailure(e.response?.status === 503 ? "maintenance" : "network");
      } finally {
        setCheckedSession(true);
      }
    })();
  }, [history]);

  // 「ログイン直後」に一回だけ遷移（ボタン/外部ログイン成功時のため）
  React.useEffect(() => {
    if (!session.logged_in) return;
    // ここでは pending が残っていれば最優先
    const dest = pendingRef.current ?? "/mypage";
    history.replace(dest);
  }, [session.logged_in, history]);

  if (!checkedSession) return <Loading />;
  if (loadFailure) return <AppLoadError kind={loadFailure} />;

  return (
    <EntranceTemplate title="ログイン">
      <Box className={classes.social}>
        <GoogleLoginButton v2 labelSize={isMobile ? 14 : 16} disabled={submitting} onSuccess={handleSuccessGoogle} onError={handleError} />
        {Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && (
          <AppleLoginButton v2 disabled={submitting} onSuccess={handleSuccessApple} onError={handleError} />
        )}
      </Box>
      <Box className={classes.divider}>または</Box>
      <form onSubmit={handleSubmit(onClickLogin)} noValidate>
        <Box className={classes.fields}>
          <Controller
            name="email"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="メールアドレス"
                inputMode="email"
                autoComplete="email"
                disabled={submitting}
                error={!!errors.email}
                errorText={errors.email?.message}
                placeHolder="propally@example.com"
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="パスワード"
                type="password"
                autoComplete="current-password"
                disabled={submitting}
                error={!!errors.password}
                errorText={errors.password && <span dangerouslySetInnerHTML={{ __html: errors.password.message }} />}
              />
            )}
          />
          <Box textAlign="center">
            <TextLink arrow={false} href="/password_reset/new">
              パスワードを忘れた場合はこちら
            </TextLink>
          </Box>
        </Box>
        <EntranceActions>
          <AuthErrorSummary
            message={error}
            description={
              error === "メールアドレスもしくはパスワードが違います"
                ? "入力をお確かめの上、もう一度お試しください。"
                : undefined
            }
          />
          <CustomButton type="submit" buttonSize="L" fullWidth disabled={submitting}>
            ログイン
          </CustomButton>
        </EntranceActions>
      </form>
      <Box className={classes.signup}>
        <Typography component="span" style={{ fontSize: "inherit" }} color="textSecondary">
          アカウントがまだの方は
        </Typography>
        <TextLink href="/registration_intro">はじめてみる</TextLink>
      </Box>
    </EntranceTemplate>
  );
};
